// @flow

import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import ListPage from 'esm-web-pro/containers/pages/ListPage';
import { bindActionCreators } from 'redux';
import { addBreadcrumbs, removeBreadcrumb } from 'esm-web-pro/redux/breadcrumb';
import { connect, useDispatch } from 'react-redux';
import withRouter from 'speed-js-react/src/router/withRouter';
import { APIContext, connectAPI } from 'speed-js-react';
import { CheckBox } from 'grommet';
import RolesSidePanel from 'esm-web-pro/components/pages/roles/RolesSidePanel';
import ApiClient from 'speed-js-core/src/api/ApiClient';

const ROLES_SIDEPANEL_KEY = 'ROLES_SIDEPANEL_KEY';

type Props = {
    match: Object,
    addBreadcrumbs: (title: string, link: string) => void,
    removeBreadcrumb: (title: string) => void,
    check: (id: string, query: Object) => Promise<Object>,
};

const messages = defineMessages({
    roles: {
        id: 'authorization-role',
        defaultMessage: 'Roles',
    },
});

const RolesDashboard = (props: Props) => {
    const {
        match: { params: { id: objectId } }, addBreadcrumbs: addBreadcrumbsAction,
        removeBreadcrumb: removeBreadcrumbAction, check,
    } = props;

    const [role, setRole] = React.useState(null);
    const { formatMessage } = useIntl();
    const { apiClient } = React.useContext(APIContext);
    const dispatch = useDispatch();

    const listRef = React.useRef(null);

    const reloadTable = React.useCallback(() => {
        if (listRef.current) {
            listRef.current.table.current.reloadData();
        }
    }, []);

    const reloadRole = React.useCallback(() => {
        apiClient.resources.role.get(objectId).then((response) => {
            setRole(response);
        });
    }, [apiClient, objectId]);

    const handleCheckboxChange = React.useCallback((row) => {
        const permissionId = row.original.id;
        check(objectId, permissionId).then(() => {
            reloadTable();
        });
    }, [check, objectId, reloadTable]);

    React.useEffect(() => {
        reloadRole();
    }, [reloadRole]);

    React.useEffect(() => {
        if (role) {
            dispatch(addBreadcrumbsAction([
                {
                    title: formatMessage(messages.roles),
                    link: '/administration/authorisation/roles',
                },
                {
                    title: role.name,
                    link: `/administration/authorisation/roles/${objectId}/dashboard/`,
                },
            ]));
        }

        return () => {
            if (role) {
                dispatch(removeBreadcrumbAction(role.name));
                dispatch(removeBreadcrumbAction(formatMessage(messages.roles)));
            }
        };
    }, [addBreadcrumbsAction, removeBreadcrumbAction, role, dispatch, objectId, formatMessage]);

    if (!role) {
        return null;
    }

    return (
        <>
            <RolesSidePanel
                object={role}
                reload={reloadRole}
                panelId={ROLES_SIDEPANEL_KEY}
            />
            <ListPage
                {...props}
                ref={listRef}
                resourceName="permission"
                permissions={['auth.view_permission']}
                additionalQuery={{ role_permissions_uuid: role.uuid }}
                columns={[
                    {
                        Header: <FormattedMessage id="authorization-permissions-name" defaultMessage="Name" />,
                        accessor: 'name',
                    },
                    {
                        Header: <FormattedMessage
                            id="authorization-permissions-codename"
                            defaultMessage="Code name"
                        />,
                        accessor: 'codename',
                    },
                    {
                        Header: <FormattedMessage
                            id="authorization-permissions-have-permission"
                            defaultMessage="Have Permission"
                        />,
                        accessor: 'has_rights',
                        Cell: (rowProps) => (
                            <CheckBox
                                onChange={() => handleCheckboxChange(rowProps)}
                                checked={rowProps.original.checked}
                            />
                        ),
                        minWidth: 70,
                    },
                ]}
            />
        </>
    );
};

const apiCallback = (apiClient: ApiClient) => ({
    check: {
        send: (id: string, query: Object) => apiClient.resources.role.check(id, query),
    },
});

const mapDispatchToProps = ((dispatch) => (bindActionCreators({ addBreadcrumbs, removeBreadcrumb }, dispatch)));

export default connectAPI(apiCallback)(withRouter(connect(null, mapDispatchToProps)(RolesDashboard)));
